import React, { useState } from 'react';
import { useLocation, useParams } from 'wouter';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import MyContainer from '@/components/MyContainer';
import { SaveButton } from '@/components/ui/buttons/save-button';
import type { UserFormValues } from '@/api/types';
import { useToast } from '@/hooks/use-toast';
import { useUpdateUser, useDeleteUser, useUser } from '@/services/user-service';
import { UserForm } from '@/components/users/user-form';
import { useUserForm } from './hooks/use-user-form';

export default function EditUserPage() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const userId = params.id ?? null;

  const { data: userResponse, isLoading, error } = useUser(userId);
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { form, handleSave } = useUserForm({
    mode: 'update',
    user: userResponse ?? null,
  });

  const handleSubmit = async (values: UserFormValues) => {
    if (!userId) {
      throw new Error('User id is required');
    }

    const response = await updateUserMutation.execute({
      id: userId,
      data: values,
    });

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update user');
    }

    return response.data;
  };

  const handleSuccess = () => {
    setLocation('/users');
  };

  const handleCancel = () => {
    setLocation('/users');
  };

  const handleDelete = async () => {
    if (!userId) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await deleteUserMutation.execute(userId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete user');
      }

      toast({
        title: 'User deleted',
        description: 'The user has been removed successfully.',
      });

      setIsDeleteDialogOpen(false);
      setLocation('/users');
    } catch (err: any) {
      toast({
        title: 'Delete failed',
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !userResponse || !userId) {
    return (
      <MyContainer
        title="Edit user"
        subtitle="Unable to load user details."
        actions={
          <Button variant="contained" onClick={() => setLocation('/users')}>
            Back to list
          </Button>
        }
      >
        <Alert severity="error">
          <AlertTitle>Unable to load user</AlertTitle>
          {error?.message || 'Please try again later.'}
        </Alert>
      </MyContainer>
    );
  }

  return (
    <>
      <MyContainer
        title={`Edit user: ${userResponse.username}`}
        subtitle="Update user information and preferences."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <SaveButton disabled={form.formState.isSubmitting} handleSave={handleSave} />
          </Stack>
        }
        footer={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </Stack>
          </Box>
        }
      >
        <UserForm
          form={form}
          mode="update"
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </MyContainer>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-user-title"
      >
        <DialogTitle id="delete-user-title">Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
